import { Alert } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';

import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Helmet } from 'react-helmet';

import { CinemaCard } from 'widgets/cards';
import { Cinema, useCinema } from 'api/cinemaClub';

import { Box, Button, CustomBox, GridChild, GridContainer, Title } from 'common';

import { useLanguage } from '../../context/LanguageContext';
import { cinemaSinglePageLabels } from '../../locales/translations';

import { CinemaImagesCarousel } from './CinemaImagesCarousel'

export const SingleCinemaPage = () => {
    const { id } = useParams();
    const { onGetAllCinema, onGetOneCinema } = useCinema();

    const [cinemaDetails, setCinemaDetails] = useState<Cinema | null>(null);
    const [latestCinema, setLatestCinema] = useState<Cinema[]>([]);
    const [loadingDetails, setLoadingDetails] = useState(true);
    const [loadingLatestCinema, setLoadingLatestCinema] = useState(true);
    const [error, setError] = useState<string>('');

    const { language } = useLanguage();
    const latestCinemaText = cinemaSinglePageLabels.latestCinema[language];
    const allCinemasText = cinemaSinglePageLabels.allCinemasButton[language];

    useEffect(() => {
        const fetchCinemaDetails = async () => {
            try {
                const result = await onGetOneCinema(Number(id));
                if (result.success) {
                    setCinemaDetails(result.data);
                } else {
                    setError('Не удалось загрузить детали новости.');
                }
            } catch (error) {
                setError('Произошла ошибка при запросе деталей новости.');
            } finally {
                setLoadingDetails(false);
            }
        };

        const fetchLatestCinema = async () => {
            try {
                const result = await onGetAllCinema();
                if (result.success) {
                    const sortedCinema = result.data.sort((a: Cinema, b: Cinema) =>
                        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                    );
                    setLatestCinema(sortedCinema);
                } else {
                    setError('Произошла ошибка при загрузке списка новостей.');
                }
            } catch (error) {
                setError('Произошла ошибка при запросе списка новостей.');
            } finally {
                setLoadingLatestCinema(false);
            }
        };

        fetchCinemaDetails();
        fetchLatestCinema();
    }, [id, onGetOneCinema, onGetAllCinema]);

    if (loadingDetails && loadingLatestCinema)
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );

    if (error) return <Alert severity="error">{error}</Alert>;

    return (
        <Box>
            <GridContainer>
                <GridChild md={8}>
                    {cinemaDetails && (
                        <CustomBox title={cinemaDetails[`title_${language}`]}>
                            <Helmet>
                                <title>{cinemaDetails[`title_${language}`]}</title>
                                <meta name="description" content="Информация о библиотеке AlmaU Open Library" />
                            </Helmet>
                            {cinemaDetails.images && cinemaDetails.images.length > 0 && cinemaDetails.images[0] ? (
                                <CinemaImagesCarousel cinemaItem={cinemaDetails} />
                            ) : (<ImageNotSupportedIcon
                                sx={{
                                    height: 200,
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '5rem'
                                }}
                            />)}
                            <Title gutterBottom variant="h5" sx={{ fontWeight: 600, marginTop: 2 }}>
                                {cinemaDetails[`title_${language}`]}
                            </Title>
                            <Title gutterBottom variant="body2" color="text.secondary">
                                <div dangerouslySetInnerHTML={{
                                    __html: cinemaDetails[`description_${language}`]
                                }} />
                            </Title>
                            <Title variant="body2" color="text.primary">
                                {dayjs(cinemaDetails.created_at).format('DD.MM.YYYY')}
                            </Title>
                        </CustomBox>
                    )}
                </GridChild>

                <GridChild md={4}>
                    {!loadingLatestCinema && (
                        <CustomBox title={latestCinemaText}>
                            {latestCinema.slice(0, 3).map((cinemaItem) => (
                                <CinemaCard key={cinemaItem.id} cinemaItem={cinemaItem} />
                            ))}

                            <Box sx={{ paddingTop: 6, display: 'flex', justifyContent: 'start' }}>
                                <Button variant="contained" color="error" href="/cinema-club">
                                    {allCinemasText}
                                </Button>
                            </Box>
                        </CustomBox>
                    )}
                </GridChild>
            </GridContainer>
        </Box>
    );
};
